import type { CT_STATS, CtAttributionSegment, CtCallRow, CtNumberRow, CtStat, CtTone } from '@elva/ui';
import type { CtHandoffKey, CtOutcomeKey, CtSourceKey } from '@elva/ui';
import { growthGet, growthGetBlob, growthList, growthPost } from '@/data/http';
import { defaultMarketingPeriod } from '@/data/marketingScope';
import { downloadBlob } from '@/data/download';
import type { BeCall, BeCallSummary, BeTrackingPool, MarketingCallOutcome } from '@/data/marketingApiTypes';
import { deepCamel, enumLower, num, stableNumericId, str } from '@/data/adapt';

import type { MarketingMeta } from '@/data/marketingApiTypes';

export const CALL_TRACKING_PAGE_SIZE = 25;

export interface CallTrackingPagination {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface CallTrackingData {
  items: CtCallRow[];
  idMap: Record<number, string>;
  stats: typeof CT_STATS;
  attribution: CtAttributionSegment[];
  numberPool: CtNumberRow[];
  pagination: CallTrackingPagination;
}

const UI_SOURCE_KEYS: CtSourceKey[] = [
  'ad_click_to_call',
  'landing_page_ads',
  'landing_page_sms',
];

const WIRE_SOURCE_KEYS: CtSourceKey[] = [
  'google_ads',
  'gbp',
  'organic',
  'direct',
  'unknown',
];

const CT_OUTCOME_KEYS: CtOutcomeKey[] = ['booked', 'callback', 'info_only', 'spam'];

const CT_HANDOFF_KEYS: CtHandoffKey[] = [
  'ai_closed',
  'warm_transfer',
  'no_answer',
  'abandoned',
  'unknown',
];

const SOURCE_TONE: Record<CtSourceKey, CtTone> = {
  ad_click_to_call: 'terra',
  landing_page_ads: 'info',
  landing_page_sms: 'iris',
  google_ads: 'success',
  gbp: 'info',
  organic: 'iris',
  direct: 'neutral',
  unknown: 'neutral',
};

function normalizeSourceKey(source?: string | null): CtSourceKey {
  const key = (source ?? 'unknown').toLowerCase().replace(/-/g, '_');
  if ([...UI_SOURCE_KEYS, ...WIRE_SOURCE_KEYS].includes(key as CtSourceKey)) {
    return key as CtSourceKey;
  }
  return 'unknown';
}

function adaptOutcome(value: string | null | undefined): CtOutcomeKey {
  const key = enumLower(value, 'info_only');
  return CT_OUTCOME_KEYS.includes(key as CtOutcomeKey) ? (key as CtOutcomeKey) : 'info_only';
}

/** Live serializer omits `handoff` — never infer from other fields. */
function adaptHandoff(row: BeCall): CtHandoffKey {
  const key = enumLower(row.handoff, '');
  if (CT_HANDOFF_KEYS.includes(key as CtHandoffKey)) return key as CtHandoffKey;
  return 'unknown';
}

function formatCallTime(iso: string | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '—';

  const now = new Date();
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (date.toDateString() === now.toDateString()) return `Today · ${time}`;

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return `Yesterday · ${time}`;

  const day = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${day} · ${time}`;
}

function formatDurationSeconds(seconds: number | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '—';
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}m ${String(remainder).padStart(2, '0')}s`;
}

function adaptDuration(row: BeCall): string {
  if (row.duration_seconds != null) return formatDurationSeconds(row.duration_seconds);
  if (typeof row.duration === 'number') return formatDurationSeconds(row.duration);
  if (row.duration != null && row.duration !== '') return str(row.duration);
  return '—';
}

function adaptCampaign(row: BeCall): string {
  const name = str(row.campaign ?? row.campaign_name, '');
  if (name && name !== '—') return name;
  if (row.campaign_id != null) return `Campaign #${row.campaign_id}`;
  return '—';
}

function callerDisplayName(row: BeCall): string {
  const name = row.matched_patient_name ?? row.caller_name ?? row.patient_name;
  if (name != null && String(name).trim()) return str(name);
  return 'Unknown';
}

function unwrapCallRow(payload: BeCall | { item?: BeCall }): BeCall {
  if (payload && typeof payload === 'object' && 'item' in payload && payload.item) {
    return payload.item;
  }
  return payload as BeCall;
}

function adaptCall(row: BeCall): { call: CtCallRow; backendId: string } {
  const backendId = String(row.id ?? '');
  const outcome = adaptOutcome(row.outcome);
  const recordingUrl = str(row.recording_url ?? row.transcript_url, '') || null;

  return {
    backendId,
    call: {
      id: stableNumericId(backendId),
      caller: str(row.from_phone_display ?? row.from_phone_e164 ?? row.caller),
      callerName: callerDisplayName(row),
      time: formatCallTime(row.call_started_at ?? row.started_at ?? row.time),
      duration: adaptDuration(row),
      source: normalizeSourceKey(row.source),
      campaign: adaptCampaign(row),
      number: str(row.to_phone_display ?? row.to_phone_e164 ?? row.number ?? row.tracking_number),
      outcome,
      handoff: adaptHandoff(row),
      intent: row.intent != null && String(row.intent).trim() ? str(row.intent) : '—',
      ...(recordingUrl ? { recordingUrl } : {}),
    },
  };
}

function emptyCallStats(): typeof CT_STATS {
  return [
    { key: 'total', label: 'Total Ad Calls', value: '0', sub: 'Last 30 days', tone: 'neutral' },
    {
      key: 'booked',
      label: 'Booked from Calls',
      value: '0',
      suffix: '0%',
      sub: 'Confirmed via PMS match',
      tone: 'success',
    },
    {
      key: 'callbacks',
      label: 'Pending Callbacks',
      value: '0',
      sub: 'Awaiting follow-up',
      tone: 'gold',
    },
    {
      key: 'duration',
      label: 'Avg Call Duration',
      value: '—',
      sub: 'Across all ad calls',
      tone: 'neutral',
    },
  ];
}

function adaptStatsFromSummary(summary: BeCallSummary | undefined): typeof CT_STATS {
  if (!summary?.tiles) return emptyCallStats();

  const callbacks = num(summary.tiles.pending_callbacks);
  const callbacksStat: CtStat = {
    key: 'callbacks',
    label: 'Pending Callbacks',
    value: str(summary.tiles.pending_callbacks),
    sub: 'Awaiting follow-up',
    tone: 'gold',
  };
  if (callbacks > 0) callbacksStat.link = 'View in Human-in-Loop';

  return [
    {
      key: 'total',
      label: 'Total Ad Calls',
      value: str(summary.tiles.total_ad_calls),
      sub: 'Last 30 days',
      tone: 'neutral',
    },
    {
      key: 'booked',
      label: 'Booked from Calls',
      value: str(summary.tiles.booked?.value),
      suffix:
        summary.tiles.booked?.rate != null
          ? `${Math.round(num(summary.tiles.booked.rate) * 100)}%`
          : undefined,
      sub: str(summary.tiles.booked?.note, 'Confirmed via PMS match'),
      tone: 'success',
    },
    callbacksStat,
    {
      key: 'duration',
      label: 'Avg Call Duration',
      value: str(summary.tiles.avg_call_duration, '—'),
      sub: 'Across all ad calls',
      tone: 'neutral',
    },
  ].map((stat) => {
    const out: CtStat = {
      key: stat.key,
      label: stat.label,
      value: stat.value,
      sub: stat.sub,
      tone: stat.tone as CtStat['tone'],
    };
    if (stat.suffix) out.suffix = stat.suffix;
    if ('link' in stat && stat.link) out.link = stat.link;
    return out;
  }) as typeof CT_STATS;
}

function adaptPagination(
  meta: MarketingMeta | undefined,
  page: number,
  pageSize: number,
  itemCount: number,
  summaryTotal?: number,
): CallTrackingPagination {
  const totalCount = num(meta?.total_count, summaryTotal ?? itemCount);
  const totalPages = Math.max(1, num(meta?.total_pages, Math.ceil(totalCount / pageSize)));
  return {
    page: num(meta?.page, page),
    pageSize: num(meta?.page_size, pageSize),
    totalCount,
    totalPages,
  };
}
function adaptAttribution(summary: BeCallSummary | undefined): CtAttributionSegment[] {
  return (summary?.attribution ?? []).map((segment) => {
    const key = normalizeSourceKey(segment.key);
    return {
      key,
      label: str(segment.label),
      value: num(segment.value),
      tone: SOURCE_TONE[key],
    };
  });
}

export async function fetchCallTracking(options?: {
  page?: number;
  limit?: number;
  period?: string;
}): Promise<CallTrackingData> {
  const period = options?.period ?? defaultMarketingPeriod();
  const page = options?.page ?? 1;
  const limit = options?.limit ?? CALL_TRACKING_PAGE_SIZE;

  const [{ items: rows, meta }, summary, numberPool] = await Promise.all([
    growthList<BeCall>('/calls/', { page, limit }),
    growthGet<BeCallSummary>('/calls/summary/', { period }).catch(() => undefined),
    fetchTrackingNumbers(),
  ]);

  const items: CtCallRow[] = [];
  const idMap: Record<number, string> = {};

  for (const row of rows) {
    const { call, backendId } = adaptCall(row);
    items.push(call);
    idMap[call.id] = backendId;
  }

  const pagination = adaptPagination(
    meta,
    page,
    limit,
    items.length,
    summary?.tiles?.total_ad_calls,
  );

  return {
    items,
    idMap,
    stats: adaptStatsFromSummary(summary),
    attribution: adaptAttribution(summary),
    numberPool,
    pagination,
  };
}

export async function fetchTrackingNumbers(): Promise<CtNumberRow[]> {
  const data = await growthGet<BeTrackingPool>('/tracking-numbers/').catch(() => undefined);

  if (!data) return [];
  const rows = (data.items ?? []) as Array<Record<string, unknown>>;
  return rows.map((row): CtNumberRow => {
    const c = deepCamel<Record<string, unknown>>(row);
    const status = enumLower(c.status as string, 'available');
    return {
      number: str(c.number),
      status: ['assigned', 'reserved', 'available'].includes(status)
        ? (status as CtNumberRow['status'])
        : 'available',
      assignedTo: str(c.assignedTo, '—'),
      source: normalizeSourceKey(c.source as string | undefined),
      assignedDate: str(c.assignedDate, '—'),
      callsThisMonth: num(c.callsThisMonth),
      ...(c.reservedUntil ? { reservedUntil: str(c.reservedUntil) } : {}),
    };
  });
}

export async function fetchCallDetail(id: string): Promise<CtCallRow> {
  const payload = await growthGet<BeCall | { item?: BeCall }>(`/calls/${encodeURIComponent(id)}/`);
  const row = unwrapCallRow(payload);
  const { call } = adaptCall({ ...row, id });
  return call;
}

export async function classifyCall(id: string, outcome: MarketingCallOutcome) {
  return growthPost<{ lead_id?: number | string; status?: string }>(
    `/calls/${id}/manual-classify/`,
    { outcome },
  );
}

export async function exportCallsCsv(): Promise<void> {
  const blob = await growthGetBlob('/calls/export/', { format: 'csv' });
  downloadBlob(blob, 'calls-export.csv');
}
